import { createSharedComposable } from '@vueuse/core';
import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import {
    useAcademyTrainingsListingFetch,
    useAcademyTrainingsListingPageFetch
} from '~/composables/useAcademyTrainingsListingFetch';
import type { AcademyTrainingsListingResponse } from '~/types/academy/academy';
import type { AcademyTrainingsListingSort } from '~/types/academy/trainings-listing-sort';
import type { AcademyFilter, AcademyFilterState } from '~/types/academy/filters';
import {
    buildAcademyTrainingsListingQuery,
    mapFilterStateToTrainingsListingQuery
} from '~/utils/academy/trainings/buildTrainingsListingQuery';
import { withResolvedCtaUrl, withTeaserColor } from '~/utils/academy/trainings/enrichTrainingsListingItem';
import type { FILTER_FIELDS } from '~/utils/academy/constants';

export type AcademyTrainingsFixedFilters = Partial<Record<(typeof FILTER_FIELDS)[number], string>>;

export const MAX_OCCURRENCES_PER_EVENT = 8;
export const STATUS_FILTER_CODE = 'status';
export const DEFAULT_CATALOG_SORT: AcademyTrainingsListingSort = {
    key: 'createdAt',
    direction: 'desc'
};

/**
 * A page appended by "load more", held as the async data entry it was registered under rather than
 * as a copy of its items: a refresh writes back into `data`, and the list follows.
 */
type LoadedListingPage = {
    key: string;
    data: Ref<AcademyTrainingsListingResponse | null | undefined>;
};

const isRenderableCheckboxFilter = (filter: AcademyFilter): boolean => {
    if (filter.type === 'checkbox_filter') {
        return Array.isArray(filter.params.options) && filter.params.options.length > 0;
    }

    return false;
};

const createAcademyFilterState = (filters: AcademyFilter[]): AcademyFilterState =>
    filters.reduce<AcademyFilterState>((state, filter) => {
        if (isRenderableCheckboxFilter(filter)) {
            state[filter.code] = {
                type: 'checkbox_filter',
                code: filter.code,
                selectedKeys: []
            };
        }

        return state;
    }, {});

function _useAcademyTrainingsListing() {
    const localizedPath = useLocalizedPath();
    const academyPath = useRequiredSectionPathById('academy');
    const { urlSegment } = useLanguage();

    const filterState = ref<AcademyFilterState>({});
    const catalogSort = ref<AcademyTrainingsListingSort>({ ...DEFAULT_CATALOG_SORT });
    const rootFolderSource = shallowRef<MaybeRefOrGetter<string | undefined>>();
    const resolvedRootFolder = computed(() => {
        const folder = rootFolderSource.value ? toValue(rootFolderSource.value) : undefined;
        return folder?.trim() || undefined;
    });

    function setRootFolder(folder: MaybeRefOrGetter<string | undefined>) {
        rootFolderSource.value = folder;
    }

    const pageSizeSource = shallowRef<MaybeRefOrGetter<number | undefined>>();
    const resolvedPageSize = computed(() => (pageSizeSource.value ? toValue(pageSizeSource.value) : undefined));

    function setPageSize(size: MaybeRefOrGetter<number | undefined>) {
        pageSizeSource.value = size;
    }

    /** Query params applied to every fetch, independent of the user-facing filter state (e.g. preset type filters). */
    const fixedFiltersSource = shallowRef<MaybeRefOrGetter<AcademyTrainingsFixedFilters | undefined>>();
    const resolvedFixedFilters = computed(() =>
        fixedFiltersSource.value ? toValue(fixedFiltersSource.value) : undefined
    );

    function setFixedFilters(filters: MaybeRefOrGetter<AcademyTrainingsFixedFilters | undefined>) {
        fixedFiltersSource.value = filters;
    }

    const listingQuery = computed(() => ({
        max_occurrences_per_event: MAX_OCCURRENCES_PER_EVENT,
        ...resolvedFixedFilters.value,
        filterState: filterState.value,
        sort_by: catalogSort.value.key,
        sort_by_direction: catalogSort.value.direction,
        ...(resolvedPageSize.value ? { page_size: resolvedPageSize.value } : {}),
        ...(resolvedRootFolder.value ? { root_folder: resolvedRootFolder.value } : {})
    }));
    const listingQuerySignature = computed(() => JSON.stringify(buildAcademyTrainingsListingQuery(listingQuery.value)));

    const { data: listingData, error, pending } = useAcademyTrainingsListingFetch(listingQuery);
    const fetchListingPage = useAcademyTrainingsListingPageFetch();

    const loadedPage = ref(1);
    // Shallow so the entries keep their `data` refs intact - a deep ref would unwrap them.
    const appendedPages = shallowRef<LoadedListingPage[]>([]);
    const loadingMore = ref(false);

    const filterOptions = computed(() => listingData.value?.filterOptions ?? []);
    const visibleFilters = computed(() => filterOptions.value.filter(isRenderableCheckboxFilter));

    const prettyError = computed(() => (error.value ? JSON.stringify(error.value, null, 2) : null));
    const showActions = computed(() => !pending.value && !prettyError.value && visibleFilters.value.length > 0);

    const hasActiveFilters = computed(
        () => Object.keys(mapFilterStateToTrainingsListingQuery(filterState.value)).length > 0
    );

    const firstPageItems = computed(() => listingData.value?.items ?? []);
    const appendedItems = computed(() => appendedPages.value.flatMap((page) => page.data.value?.items ?? []));
    const items = computed(() =>
        [...firstPageItems.value, ...appendedItems.value]
            .map(withTeaserColor)
            .map((item) => withResolvedCtaUrl(item, localizedPath, urlSegment.value, academyPath.value))
    );
    const totalCount = computed(() => listingData.value?.total_count ?? 0);
    const hasMoreItems = computed(() => items.value.length > 0 && items.value.length < totalCount.value);
    const isInitialLoading = computed(() => Boolean(pending.value) && items.value.length === 0);
    const showNoResults = computed(
        () => !pending.value && listingData.value != null && !error.value && items.value.length === 0
    );

    function initializeFilterState(filters: AcademyFilter[]) {
        filterState.value = createAcademyFilterState(filters);
    }

    function syncFilterState(filters: AcademyFilter[]) {
        const nextState = createAcademyFilterState(filters);

        for (const [code, value] of Object.entries(filterState.value)) {
            if (nextState[code]?.type === value.type) {
                nextState[code] = value;
            }
        }

        filterState.value = nextState;
    }

    watch(
        filterOptions,
        (filters) => {
            if (!filters.length) {
                return;
            }

            syncFilterState(filters);
        },
        { immediate: true }
    );

    watch(listingQuerySignature, () => {
        // These entries describe a result set that no longer exists. Left registered they would sit
        // in `payload.data` and a refresh would refetch pages that are not on screen any more.
        if (appendedPages.value.length) {
            clearNuxtData(appendedPages.value.map((page) => page.key));
            appendedPages.value = [];
        }

        loadedPage.value = 1;
    });

    function toggleCheckboxes(filterCode: string, selectedKeys: string[]) {
        const value = filterState.value[filterCode];

        if (!value || value.type !== 'checkbox_filter') {
            return;
        }

        filterState.value = {
            ...filterState.value,
            [filterCode]: {
                ...value,
                selectedKeys
            }
        };
    }

    function toggleCheckboxOption(filterCode: string, optionKey: string) {
        const value = filterState.value[filterCode];

        if (!value || value.type !== 'checkbox_filter') {
            return;
        }

        const keys = new Set(value.selectedKeys);

        if (keys.has(optionKey)) {
            keys.delete(optionKey);
        } else {
            keys.add(optionKey);
        }

        toggleCheckboxes(filterCode, [...keys]);
    }

    function toggleCheckboxesInverse(filterCode: string, excludedKeys: string[]) {
        const options = filterOptions.value.find((filter) => filter.code === filterCode)?.params.options ?? [];
        const selectedKeys = options.map((option) => option.key).filter((key) => !excludedKeys.includes(key));

        toggleCheckboxes(filterCode, selectedKeys);
    }

    function resetFilters() {
        if (!filterOptions.value.length) {
            return;
        }

        initializeFilterState(filterOptions.value);
    }

    async function loadMore() {
        if (loadingMore.value || pending.value || !hasMoreItems.value) {
            return;
        }

        loadingMore.value = true;
        const pageToLoad = loadedPage.value + 1;
        const requestSignature = listingQuerySignature.value;

        try {
            const pageFetch = fetchListingPage({
                ...listingQuery.value,
                page: pageToLoad
            });

            await pageFetch;

            // A filter change while this was in flight retargets the list, so the page it answered
            // for is gone. An empty or failed page has nothing to append either; both leave an entry
            // behind that only the refresh would find, so drop it rather than keep it registered.
            if (listingQuerySignature.value !== requestSignature || !pageFetch.data.value?.items?.length) {
                clearNuxtData(pageFetch.key);
                return;
            }

            appendedPages.value = [...appendedPages.value, { key: pageFetch.key, data: pageFetch.data }];
            loadedPage.value = pageToLoad;
        } finally {
            loadingMore.value = false;
        }
    }

    return {
        setRootFolder,
        setPageSize,
        setFixedFilters,
        filterState,
        catalogSort,
        filterOptions,
        visibleFilters,
        prettyError,
        showActions,
        hasActiveFilters,
        toggleCheckboxes,
        toggleCheckboxOption,
        toggleCheckboxesInverse,
        resetFilters,
        items,
        totalCount,
        hasMoreItems,
        isInitialLoading,
        loadingMore,
        showNoResults,
        loadMore,
        error,
        pending
    };
}

export const useAcademyTrainingsListing = createSharedComposable(_useAcademyTrainingsListing);

/** Non-shared variant for self-contained bloks (e.g. carousels) that must not clash with the shared listing state. */
export const useScopedAcademyTrainingsListing = _useAcademyTrainingsListing;
